<template>
  <div v-if="shouldDisplayPickers">
    <ClientOnly>
      <PickerGroup
        v-for="group in pickersWithLoadingAndColor"
        :key="group.id"
        class="mb-32"
        :picker="group"
        @change="handleChange"
      >
        <template #modal v-if="hasModal(group.id)">
          <component :is="getModal(group.id)" />
        </template>
        <template #extra>
          <slot :name="`extra-${group.id}`" />
          <NewBattery
            v-if="group.id === 'grades'"
            :arrow-position="arrowPosition"
            :offer-type="newBattery?.offerType"
            :product-category="trackingDetails.category3MarketplaceCategory_ID"
            :status="newBattery?.status"
            @change="handleNewBatteryChange"
          />
        </template>
        <template #fallback>
          <Skeleton
            :pickers="pickers"
            :should-dsplay-new-battery="shouldDisplayNewBattery"
          />
        </template>
      </PickerGroup>
    </ClientOnly>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch, watchEffect } from 'vue'

import { productAPI } from '@backmarket/http-api'

import { ARROW_POSITIONS } from '../Arrow/Arrow.constants'

import { PICKERS_MODALS } from './Pickers.constants'
import NewBattery from './components/NewBattery/NewBattery.vue'
import PickerGroup from './components/PickerGroup/PickerGroup.vue'
import Skeleton from './components/Skeleton/Skeleton.vue'

const props = defineProps<{
  currentGradeIndex: number | null
  newBattery?: productAPI.GetBestOffersResponse[number]['newBattery']
  pickers: productAPI.GetPickersResponse['pickers']
  trackingDetails: productAPI.GetProductResponse['trackingDetails']
}>()

const emit = defineEmits(['change', 'emphasis-change'])
const loadingGroup = ref('')
const loadingIndex = ref<number | undefined>(undefined)

watch(props, () => {
  loadingGroup.value = ''
  loadingIndex.value = undefined
})

const shouldDisplayPickers = computed(() => {
  return props.pickers.length > 0
})

const arrowPosition = computed(() => {
  return props.currentGradeIndex
    ? ARROW_POSITIONS[props.currentGradeIndex]
    : ARROW_POSITIONS[0]
})

const selectedMasterPicker = computed(() => {
  const pickerGroup =
    props.pickers.find(({ id }) => id === 'recommended_variants')?.items ?? []

  return pickerGroup.find(({ selected }) => !!selected)
})

const shouldDisplayNewBattery = computed(() => {
  return props.trackingDetails.category3MarketplaceCategory_ID === 2
})

watchEffect(() => {
  emit(
    'emphasis-change',
    selectedMasterPicker.value?.emphasis,
    selectedMasterPicker.value?.label,
  )
})

const pickersWithLoadingAndColor = computed(() => {
  return props.pickers.map((picker) => ({
    ...picker,
    items: picker.items.map((item, index) => ({
      ...item,
      loading: loadingGroup.value === picker.id && loadingIndex.value === index,
      emphasis:
        item.emphasis ||
        (item.selected ? selectedMasterPicker.value?.emphasis : undefined),
    })),
  }))
})

function getModal(id: string) {
  return PICKERS_MODALS[id]
}

function hasModal(id: string) {
  return Boolean(getModal(id))
}

function handleChange(pickerId: string, index: number) {
  loadingGroup.value = pickerId
  loadingIndex.value = index

  emit('change', pickerId, index)
}

function handleNewBatteryChange() {
  const gradePicker = pickersWithLoadingAndColor.value.find(
    (picker) => picker.id === 'grades',
  )

  const selectedGradePicker = gradePicker?.items.findIndex(
    (item) => item.selected,
  )

  loadingGroup.value = 'grades'
  loadingIndex.value = selectedGradePicker
}
</script>
